import React, { useEffect } from 'react';
import Button from '../../BaseComponents/Button';
import { useFormikContext } from 'formik';
import DatePickerComp from '../../BaseComponents/DatePicker';
import Select from '../../BaseComponents/Select';
import {
  genders,
  races,
  weightMeasurementTypes
} from '../../../utils/constants';
import { OnboardingFormValues } from '../../../types/membersTypes';
import TextInput from '../../BaseComponents/TextInput';
import { convertWeight } from '../../../utils';
import PhoneInputComp from '../../BaseComponents/PhoneInput';
import { CustomInput } from '../../location/CustomInput';
import infoIcon from '../../../assets/icons/info.svg';
import { GetMemberResponse } from '../../../types/apiGeneratedTypes';

type Step1Props = {
  setCurrentStep: (step: any) => void;
  memberData?: GetMemberResponse | null;
  saveStepData?: (stepNumber: number) => void;
  populateFormWithSavedData?: () => void;
  lastCompletedStep?: number;
};

const Step1: React.FC<Step1Props> = ({ 
  setCurrentStep, 
  memberData, 
  saveStepData, 
  populateFormWithSavedData,
  lastCompletedStep 
}) => {
  const { values, errors, setFieldValue, setFieldError } =
    useFormikContext<OnboardingFormValues>();

  const [userEditedNameFields, setUserEditedNameFields] = React.useState<Set<string>>(new Set());
  const [hasInitializedFromMemberData, setHasInitializedFromMemberData] = React.useState(false);
  
  useEffect(() => {
    if (memberData && !hasInitializedFromMemberData) {
      if (memberData.first_name && !userEditedNameFields.has('first_name') && (!values.first_name || values.first_name === '')) {
        setFieldValue('first_name', memberData.first_name);
      }
      if (memberData.last_name && !userEditedNameFields.has('last_name') && (!values.last_name || values.last_name === '')) {
        setFieldValue('last_name', memberData.last_name);
      }
      if (memberData.email) {
        setFieldValue('email', memberData.email);
      }
      if (memberData.phone) {
        setFieldValue('phone', memberData.phone);
      }
      if (memberData.dob && !values.birthDate) {
        // Convert dob string to Date object
        const dobDate = new Date(memberData.dob.endsWith('Z') ? memberData.dob : memberData.dob + 'Z');
        if (!isNaN(dobDate.getTime())) {
          setFieldValue('birthDate', dobDate);
        }
      }
      setHasInitializedFromMemberData(true);
    }
  }, [memberData, setFieldValue, userEditedNameFields, values.first_name, values.last_name, values.email, values.phone, values.birthDate, hasInitializedFromMemberData]);
  
  const handleFirstNameChange = (value: string) => {
    setFieldValue('first_name', value);
    setUserEditedNameFields(prev => new Set(prev).add('first_name'));
    
    const hasInvalidChars = /[^a-zA-Z\s]/.test(value);
    
    if (hasInvalidChars && value.trim() !== '') {
      setFieldError('first_name', 'First name can only contain letters');
    } else {
      if (errors.first_name) {
        setFieldError('first_name', '');
      }
    }
  };

  const handleLastNameChange = (value: string) => {
    setFieldValue('last_name', value);
    setUserEditedNameFields(prev => new Set(prev).add('last_name'));
    
    const hasInvalidChars = /[^a-zA-Z\s]/.test(value);
    
    if (hasInvalidChars && value.trim() !== '') {
      setFieldError('last_name', 'Last name can only contain letters');
    } else {
      if (errors.last_name) {
        setFieldError('last_name', '');
      }
    }
  };

  const [hasPopulated, setHasPopulated] = React.useState(false);
  const birthDateError = errors.birthDate as string | undefined;
  const phoneError = errors.phone as string | undefined;
  const addressError =
    (errors.full_address as string | undefined) ||
    (errors.address_line_1 as string | undefined) ||
    (errors.country as string | undefined);
  
  useEffect(() => {
    if (populateFormWithSavedData && !hasPopulated) {
      if (!values.first_name && !values.last_name) {
        populateFormWithSavedData();
      }
      setHasPopulated(true);
    }
  }, [populateFormWithSavedData, hasPopulated, values.first_name, values.last_name]);

  useEffect(() => {
    if (values.first_name && values.first_name.trim() !== '') {
      const hasInvalidChars = /[^a-zA-Z\s]/.test(values.first_name);
      
      const timeoutId = setTimeout(() => {
        if (!hasInvalidChars && errors.first_name) {
          setFieldError('first_name', '');
        }
      }, 300);
      
      return () => clearTimeout(timeoutId);
    } else {
      if (errors.first_name) {
        setFieldError('first_name', '');
      }
    }
  }, [values.first_name, errors.first_name, setFieldError]);

  useEffect(() => {
    if (values.last_name && values.last_name.trim() !== '') {
      const hasInvalidChars = /[^a-zA-Z\s]/.test(values.last_name);
      
      const timeoutId = setTimeout(() => {
        if (!hasInvalidChars && errors.last_name) {
          setFieldError('last_name', '');
        }
      }, 300);
      
      return () => clearTimeout(timeoutId);
    } else {
      if (errors.last_name) {
        setFieldError('last_name', '');
      }
    }
  }, [values.last_name, errors.last_name, setFieldError]);

  useEffect(() => {
    if (values.height && values.height.trim() !== '') {
      const hasAlphabetic = /[a-zA-Z]/.test(values.height);
      if (hasAlphabetic) {
        setFieldError('height', 'Height must contain only numbers');
      } else {
        // Clear error if no alphabetic characters found
        setFieldError('height', undefined);
      }
    } else {
      // Clear error if field is empty
      setFieldError('height', undefined);
    }
  }, [values.height, setFieldError]);

  useEffect(() => {
    if (values.weight && values.weight.trim() !== '') {
      const hasAlphabetic = /[a-zA-Z]/.test(values.weight);
      if (hasAlphabetic) {
        setFieldError('weight', 'Weight must contain only numbers');
      } else {
        setFieldError('weight', undefined);
      }
    } else {
      setFieldError('weight', undefined);
    }
  }, [values.weight, setFieldError]);

  const handleHeightChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    
    setFieldValue('height', value);
    
    const hasAlphabetic = /[a-zA-Z]/.test(value);
    
    if (hasAlphabetic) {
      setFieldError('height', 'Height must contain only numbers');
    } else {
      if (errors.height) {
        setFieldError('height', undefined);
      }
    }
  };

  const handleWeightChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    
    setFieldValue('weight', value);
    
    const hasAlphabetic = /[a-zA-Z]/.test(value);
    
    if (hasAlphabetic) {
      setFieldError('weight', 'Weight must contain only numbers');
    } else {
      if (errors.weight) {
        setFieldError('weight', undefined);
      }
    }
  };

  const handleWeightUnitChange = (newUnit: string) => {
    const numericValue = parseFloat(values.weight);
    const oldUnit = newUnit === 'kg' ? 'stones' : 'kg';
    const convertedValue = convertWeight(numericValue, oldUnit, newUnit);
    setFieldValue('weightUnit', { key: newUnit, value: newUnit });
    setFieldValue('weight', convertedValue ? convertedValue.toFixed(2) : '');
  };

  const handleAddressChange = (value: string) => {
    const city = (document.getElementsByClassName('city')[0] as HTMLInputElement)?.value;
    const postalCode = (document.getElementsByClassName('postcode')[0] as HTMLInputElement)?.value;
    const addressLine1 = (document.getElementsByClassName('line-1')[0] as HTMLInputElement)?.value;
    const country = (document.getElementsByClassName('country')[0] as HTMLInputElement)?.value;
    
    setFieldValue('full_address', value);
    setFieldValue('city', city);
    setFieldValue('postalCode', postalCode);
    setFieldValue('address_line_1', addressLine1);
    setFieldValue('country', country);
    setFieldError('full_address', undefined);
    setFieldError('city', undefined);
    setFieldError('postalCode', undefined);
    setFieldError('address_line_1', undefined);
    setFieldError('country', undefined);
  };

  const validateStepFields = () => {
    const validationErrors: Record<string, string> = {};
    const fieldErrorMessages: Record<string, string> = {
      birthDate: 'Date of Birth is required',
      first_name: 'First name is required',
      last_name: 'Last name is required',
      email: 'Email is required',
      phone: 'Mobile phone number is required',
      weight: 'Weight is required',
      height: 'Height is required',
      gender: 'Sex assigned at birth is required',
      race: 'Race/ethnicity is required',
      full_address: 'Address is required',
      postalCode: 'Postal code is required',
      city: 'City is required',
      address_line_1: 'Please select a complete address from the suggestions',
      country: 'Country is required'
    };

    const requireField = (field: keyof typeof fieldErrorMessages, isValid: boolean) => {
      if (!isValid) {
        const message = fieldErrorMessages[field];
        validationErrors[field] = message;
        setFieldError(field, message);
      }
    };

    requireField('birthDate', !!values.birthDate);
    requireField('first_name', !!values.first_name?.trim());
    requireField('last_name', !!values.last_name?.trim());
    requireField('email', !!values.email?.trim());
    requireField('phone', !!values.phone?.trim());
    requireField('weight', !!values.weight?.trim());
    requireField('height', !!values.height?.trim());
    requireField('gender', !!values.gender?.value);
    requireField('race', !!values.race?.value);
    requireField('full_address', !!values.full_address?.trim());
    requireField('postalCode', !!values.postalCode?.trim());
    requireField('city', !!values.city?.trim());
    requireField('address_line_1', !!values.address_line_1?.trim());
    requireField('country', !!values.country?.trim());

    if (errors.height) {
      validationErrors.height = errors.height as string;
    }

    if (errors.weight) {
      validationErrors.weight = errors.weight as string;
    }

    return validationErrors;
  };

  const handleNextClick = async () => {
    const validationErrors = validateStepFields();

    if (Object.keys(validationErrors).length > 0) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    try {
      if (saveStepData) {
        await saveStepData(1);
      }
    } catch {
      return;
    }

    setCurrentStep({ number: 2, name: 'Current Lifestyle' });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className='step-1'>
      <div className='form-section'>

        <div className='name-input-wrapper'>
          <TextInput
            title='First Name *'
            name='first_name'
            placeholder='Enter your first name'
            type='text'
            isError={!!errors.first_name}
            errorMessage={errors.first_name as string}
            onChange={(e) => handleFirstNameChange(e.target.value)}
            value={values.first_name}
          />
          <TextInput
            title='Last Name *'
            name='last_name'
            placeholder='Enter your last name'
            type='text'
            isError={!!errors.last_name}
            errorMessage={errors.last_name as string}
            onChange={(e) => handleLastNameChange(e.target.value)}
            value={values.last_name}
          />
        </div>
        <div className='input-wrapper' >
        <DatePickerComp
          title='Date of Birth *'
          placeholder='Enter your date of birth'
          onDateChange={(date) => {
            setFieldValue('birthDate', date);
            setFieldError('birthDate', undefined);
          }}
          selectedDate={values.birthDate}
          showTimeInput={false}
          showYearDropdown={true}
          subMaxDateDays={5840} // 16 years in days
        />
        {birthDateError && <div className='error-message'>{birthDateError}</div>}
        </div>
        <div className='select-arrow-pos1' >
        <Select
          title='Sex Assigned at Birth *'
          name='gender'
          placeholder='Select your gender'
          options={genders}
          selectedOption={values.gender}
          onChange={(option) => {
            setFieldValue('gender', option);
            setFieldError('gender', undefined);
          }}
          isError={!!errors.gender}
          errorMessage={errors.gender as string}
        />
        </div>
        <div className='phys-input-wrapper'>
          <TextInput
            title='Weight *'
            name='weight'
            placeholder='Weight'
            type='tel'
            isError={!!errors.weight}
            errorMessage={errors.weight as string}
            onChange={handleWeightChange}
            value={values.weight}
          />
          <Select
            name='weightUnit'
            options={weightMeasurementTypes}
            selectedOption={values.weightUnit}
            onChange={(option) => handleWeightUnitChange(option.key)}
            isError={!!errors.weightUnit}
          />
        </div>
        <TextInput
          title='Height (cm) *'
          name='height'
          placeholder='Enter height'
          type='tel'
          isError={!!errors.height}
          errorMessage={errors.height as string}
          onChange={handleHeightChange}
          value={values.height}
        />
        <div className='info-tooltip-wrapper' >
          <div className='title mb-12' >
            <div>Race/ethnicity* <small>(this can be relevant for your test results)</small></div>
          </div>
          <div className='select-arrow-pos' >
        <Select
          
          name='race'
          placeholder='Select your race/ethnicity'
          options={races}
          selectedOption={values.race}
          onChange={(option) => {
            setFieldValue('race', option);
            setFieldError('race', undefined);
          }}
          isError={!!errors.race}
          errorMessage={errors.race as string}
        />
        </div>
        <div className='info-tooltip-icon' >
          <div className='info-tooltip-icon-wrapper' >
          <button
            type='button'
            className='info-tooltip-icon'
            aria-label='Race ethnicity info'
            style={{ border: 'none', background: 'transparent', padding: 0, cursor: 'pointer' }}
          >
            <img width={16} height={16} src={infoIcon} alt='' />
          </button>
          <span className='info-tooltip-text' >We collect this to improve the accuracy and clinical relevance of your biomarker interpretation, as reference ranges can differ across ethnic groups.</span>
          </div>
        </div>
        </div>
        <TextInput
          title='Email *'
          name='email'
          placeholder='Enter your email'
          type='email'
          isError={!!errors.email}
          errorMessage={errors.email as string}
          value={values.email}
          isDisabled
        />
        <PhoneInputComp
          title='Mobile phone number *'
          placeholder='Enter your phone'
          selectedPhone={values.phone}
          onPhoneChange={() => {}}
          isDisabled
        />
        {phoneError && <div className='error-message'>{phoneError}</div>}
        <div className='address-search-wrapper input-wrapper'>
          <div className='text-input-title '>Address *</div>
          <CustomInput
            value={values.full_address}
            onChange={handleAddressChange}
            id="address-search-step1"
          />
          {addressError && <div className='error-message'>{addressError}</div>}
        </div>
        <TextInput
          title='City *'
          name='city'
          placeholder='Enter your city'
          type='text'
          isError={!!errors.city}
          errorMessage={errors.city as string}
          onChange={(e) => {
            setFieldValue('city', e.target.value);
            setFieldError('city', undefined);
          }}
          value={values.city}
        />
        <TextInput
          title='Postal code *'
          name='postalCode'
          id='PostalCode'
          placeholder='Enter your postal code'
          type='tel'
          isError={!!errors.postalCode}
          errorMessage={errors.postalCode as string}
          onChange={(e) => {
            setFieldValue('postalCode', e.target.value);
            setFieldError('postalCode', undefined);
          }}
          value={values.postalCode}
        />
        {/* <TextInput
          title='Postal code'
          name='postalCode'
          id='PostalCode'
          placeholder='Enter your postal code'
          type='tel'
          isError={!!errors.postalCode}
          onChange={(e) => setFieldValue('postalCode', e.target.value)}
          value={values.postalCode}
        /> */}
      </div>
      <Button
        text='Next'
        onClick={handleNextClick}
      />
    </div>
  );
};
export default Step1;